import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { authService } from '../utils/api';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage synchronously to avoid flash
  const getInitialUser = () => {
    try {
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (savedUser && token) {
        return JSON.parse(savedUser);
      }
    } catch (e) {
      console.error('Error parsing initial user:', e);
    }
    return null;
  };

  const [currentUser, setCurrentUser] = useState(getInitialUser);
  const accountId = currentUser?._id || currentUser?.id || null;
  // Memory-only navigation state, discarded on logout/account change. A refresh
  // of /auth/me for the same account must not discard a list's return context.
  // accountId deliberately owns the lifetime, even though it is not stored.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const navigationState = useMemo(() => ({ leads: null }), [accountId]);
  // Returning users (cached user + token) render INSTANTLY — the /auth/me
  // check runs in the background and silently refreshes the user object.
  // A truly-expired session still gets bounced: the api.js response
  // interceptor redirects to /login on the first 401. Blocking the whole
  // tree on that network round-trip was the first of the "4 spinners"
  // users saw on every cold open. Only a token-holder WITHOUT a cached
  // user (rare) waits for verification.
  const [loading, setLoading] = useState(() => !getInitialUser());
  const [initialized, setInitialized] = useState(false);

  // Verify user with API on mount (but we already have user from localStorage)
  useEffect(() => {
    const verifyUser = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (token) {
          // Try to verify with API and refresh user data
          try {
            const response = await authService.getCurrentUser();
            const userData = response.data?.data || response.data?.user || response.data;
            if (userData) {
              setCurrentUser(userData);
              localStorage.setItem('user', JSON.stringify(userData));
            }
          } catch (apiError) {
            // If API fails but we have cached user, keep using it
            const savedUser = localStorage.getItem('user');
            if (!savedUser) {
              // No cached user and API failed - clear everything
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setCurrentUser(null);
            } else {
              console.warn('API verification failed, using cached user data');
            }
          }
        } else {
          // No token - ensure user is null
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Authentication error:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setCurrentUser(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    verifyUser();
  }, []);

  // Login function. When `rememberMe` is true the server issues a
  // long-lived JWT (default 30 days) so the user stays signed in
  // across browser restarts.
  const login = async (username, password, rememberMe = false) => {
    try {
      const response = await authService.login(username, password, rememberMe);
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      // Store user data in localStorage for persistence
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      // Set user data
      setCurrentUser(response.data.user);
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Login with one-time code emailed to the user. `rememberMe=true`
  // asks the server for a long-lived JWT (default 30 days).
  const loginWithOtp = async (identifier, code, rememberMe = false) => {
    try {
      const response = await authService.verifyOtp(identifier, code, rememberMe);

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }

      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      setCurrentUser(response.data.user);
      return response.data;
    } catch (error) {
      console.error('OTP login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout API to invalidate token on server
      await authService.logout();
    } catch (error) {
      // Continue with local logout even if API call fails
      console.log('Server logout failed, continuing with local logout');
    }

    // Remove token and user data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear user data
    setCurrentUser(null);
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await authService.changePassword(currentPassword, newPassword);
      return response.data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  // Patch the in-memory user (and localStorage mirror) — used by the
  // profile pages after avatar upload / inline edits so the bell, the
  // sidebar avatar, and any other component reading useAuth().user
  // reflects the change immediately without a page reload.
  const updateUser = (patch) => {
    setCurrentUser(prev => {
      const next = typeof patch === 'function'
        ? patch(prev)
        : { ...(prev || {}), ...(patch || {}) };
      try {
        if (next) localStorage.setItem('user', JSON.stringify(next));
      } catch (e) {
        // localStorage may be unavailable in private mode — ignore.
      }
      return next;
    });
  };

  // Update user preferences
  const updatePreferences = async (preferences) => {
    try {
      const response = await authService.updatePreferences(preferences);
      
      // Update current user with new preferences
      setCurrentUser(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          ...preferences
        }
      }));
      
      return response.data;
    } catch (error) {
      console.error('Update preferences error:', error);
      throw error;
    }
  };

  // Context value
  const value = {
    navigationState,
    currentUser,
    user: currentUser,
    login,
    loginWithOtp,
    logout,
    changePassword,
    updateUser,
    updatePreferences,
    isAuthenticated: !!currentUser,
    loading,
    initialized
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        // Branded spinner instead of a blank white screen for the short
        // "verifying who you are" window (fresh visitors / cleared cache).
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};